import {useCallback, useEffect, useMemo} from "react";
import { Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { Form, Input, Button, Typography, Upload, Select, Card, Switch, Row, Col } from "antd";
import { 
  EnvironmentOutlined, 
  PhoneOutlined, 
  UserOutlined, 
  ClockCircleOutlined,
  ShopOutlined,
  LinkOutlined,
  PictureOutlined
} from "@ant-design/icons";

import { AppState } from "store";
import {FormItem, SelectSearch} from "components";
import {addServiceCentre, getMaintenanceServicesList} from "store/actions";
import { setModalName } from "store/actions/common-actions";
import { fileToByteArray } from "../../utils/helpers";
import { uploadIcon } from "../../svgs";
import { RcFile } from "antd/lib/upload";
import {parseZoneOptions} from "../../utils/parsers";
import {parseMaintenanceServicesList} from "../../utils/parsers/service_centers_parser";

const { Title, Text } = Typography;
const { Option } = Select;

// Generate time options for dropdown
const generateTimeOptions = () => {
  const times: string[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const hourFormatted = hour % 12 === 0 ? 12 : hour % 12;
      const minuteFormatted = minute.toString().padStart(2, "0");
      const period = hour < 12 ? "AM" : "PM";
      times.push(`${hourFormatted.toString().padStart(2, "0")}:${minuteFormatted} ${period}`);
    }
  }
  return times;
};

const timeOptions = generateTimeOptions();

const daysOfWeek = [
  { key: "monday", label: "Monday", short: "Mon" },
  { key: "tuesday", label: "Tuesday", short: "Tue" },
  { key: "wednesday", label: "Wednesday", short: "Wed" },
  { key: "thursday", label: "Thursday", short: "Thu" },
  { key: "friday", label: "Friday", short: "Fri" },
  { key: "saturday", label: "Saturday", short: "Sat" },
  { key: "sunday", label: "Sunday", short: "Sun" },
];

interface ServiceCentreFormValues {
  serviceCentreName: string;
  formattedaddress: string;
  addressName: string;
  latitude: string;
  longitude: string;
  servicesOffered: string[];
  contactName: string;
  contactNumber: string;
  googleMapLink: string;
  photo: string;
  photoExt: string;
  photoName: string;
  photo_file_raw?: RcFile;
  availability: Record<string, { open: string; closed: string; isOpen: boolean }>;
}

const initialValues: ServiceCentreFormValues = {
  serviceCentreName: "",
  formattedaddress: "",
  addressName: "",
  latitude: "",
  longitude: "",
  servicesOffered: [],
  contactName: "",
  contactNumber: "",
  googleMapLink: "",
  availability: {
    monday: { open: "10:00 AM", closed: "07:00 PM", isOpen: true },
    tuesday: { open: "10:00 AM", closed: "07:00 PM", isOpen: true },
    wednesday: { open: "10:00 AM", closed: "07:00 PM", isOpen: true },
    thursday: { open: "10:00 AM", closed: "07:00 PM", isOpen: true },
    friday: { open: "10:00 AM", closed: "07:00 PM", isOpen: true },
    saturday: { open: "10:00 AM", closed: "07:00 PM", isOpen: true },
    sunday: { open: "10:00 AM", closed: "07:00 PM", isOpen: false },
  },
  photo: "",
  photoExt: "",
  photoName: "",
  photo_file_raw: undefined,
};

// Styles - Updated for 100% width
const styles = {
  container: {
    padding: "14px",
    width: "100%",
    boxSizing: "border-box" as const,
  } as React.CSSProperties,
  
  header: {
    textAlign: "left" as const,
    marginBottom: "14px",
  } as React.CSSProperties,
  
  title: {
    marginBottom: "4px",
    color: "#1a1a2e",
  } as React.CSSProperties,
  
  subtitle: {
    color: "#6c757d",
    fontSize: "14px",
  } as React.CSSProperties,
  
  scrollContainer: {
    maxHeight: "65vh",
    overflowY: "auto" as const,
    overflowX: "hidden" as const,
    paddingRight: "8px",
    width: "100%",
  } as React.CSSProperties,
  
  sectionCard: {
    marginBottom: "16px",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
    width: "100%",
  } as React.CSSProperties,
  
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "16px",
    color: "#1a1a2e",
    fontSize: "15px",
    fontWeight: 600,
  } as React.CSSProperties,
  
  sectionIcon: {
    fontSize: "18px",
    color: "#930f0fff",
  } as React.CSSProperties,
  
  inputStyle: {
    borderRadius: "8px",
    height: "40px",
    paddingLeft: "12px",
    width: "100%",
  } as React.CSSProperties,
  
  
  textAreaStyle: {
    borderRadius: "8px",
    minHeight: "80px",
    paddingLeft: "8px",
    width: "100%",
  } as React.CSSProperties,
  
  availabilityHeader: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "20px",
    padding: "12px 16px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    marginBottom: "8px",
    width: "100%",
  } as React.CSSProperties,
  
  availabilityRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "12px",
    padding: "12px 16px",
    backgroundColor: "#fff",
    borderRadius: "8px",
    border: "1px solid #e8e8e8",
    marginBottom: "8px",
    transition: "all 0.2s ease",
    width: "100%",
  } as React.CSSProperties,
  
  availabilityRowDisabled: {
    backgroundColor: "#fafafa",
    opacity: 0.7,
  } as React.CSSProperties,
  
  dayLabel: {
    display: "flex",
    alignItems: "center",
    fontWeight: 500,
    color: "#1a1a2e",
  } as React.CSSProperties,
  
  timeSelect: {
    width: "100%",
  } as React.CSSProperties,
  
  uploadDragger: {
    borderRadius: "12px",
    border: "2px dashed #d9d9d9",
    backgroundColor: "#fafafa",
    transition: "all 0.3s ease",
    width: "100%",
  } as React.CSSProperties,
  
  actionContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "4px",
    marginTop: "18px",
    paddingTop: "4px",
    borderTop: "1px solid #f0f0f0",
    width: "100%",
  } as React.CSSProperties,
  
  cancelBtn: {
    height: "44px",
    padding: "0 32px",
    borderRadius: "8px",
    fontWeight: 500,
    fontSize: "14px",
  } as React.CSSProperties,
  
  submitBtn: {
    height: "44px",
    padding: "0 40px",
    borderRadius: "8px",
    fontWeight: 500,
    fontSize: "14px",
  } as React.CSSProperties,
};

export default function AddServiceCentreForm() {
  const dispatch = useDispatch();
  const {
    common: { organizationId },
    serviceCentres: {maintenanceServicesList},
  } = useSelector<AppState, AppState>((state) => state);
  const servicesListOptions = useMemo(() => parseMaintenanceServicesList(maintenanceServicesList), [maintenanceServicesList]);
  const onCancel = useCallback(() => {
    dispatch(setModalName(undefined));
  }, [dispatch]);

  const onSubmit = useCallback(
    (values: ServiceCentreFormValues) => {
      const latitude = parseFloat(values.latitude || "0");
      const longitude = parseFloat(values.longitude || "0");

      // Filter out closed days from availability
      const filteredAvailability = Object.fromEntries(
        Object.entries(values.availability)
          .filter(([_, time]) => time.isOpen)
          .map(([day, time]) => [day, { open: time.open, closed: time.closed }])
      );

      dispatch(
        addServiceCentre({
          organizationId: organizationId || "",
          serviceCentreName: values.serviceCentreName.trim(),
          address: {
            formattedaddress: values.formattedaddress.trim(),
            name: values.addressName.trim(),
            location: {
              latitude: isNaN(latitude) ? 0 : latitude,
              longitude: isNaN(longitude) ? 0 : longitude,
            },
          },
          servicesOffered: values.servicesOffered.toString(),
          contactName: values.contactName.trim(),
          contactNumber: values.contactNumber.trim(),
          googleMapLink: values.googleMapLink.trim(),
          photo: values.photo || "",
          photoExt: values.photo_file_raw?.type.split("/").pop() || "",
          photoName: values.photo_file_raw?.name || "",
          metaData: {
            workshop_timing: filteredAvailability,
          } as any,
        })
      );
      onCancel();
    },
    [dispatch, onCancel, organizationId]
  );

  useEffect(() => {
    dispatch(getMaintenanceServicesList())
  },[dispatch])

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {({ values, handleChange, handleSubmit, setFieldValue }) => (
        <div style={styles.container}>
          {/* Header */}
          <div style={styles.header}>
            <Title level={4} style={styles.title}>
              Add Service Center
            </Title>
            <Text style={styles.subtitle}>
              Fill in the details below to register a new service center
            </Text>
          </div>

          <Form onFinish={handleSubmit} layout="vertical" style={{ width: "100%" }}>
            <div style={styles.scrollContainer}>
              
              {/* Basic Information Section */}
              <Card style={styles.sectionCard} size="small">
                <div style={styles.sectionTitle}>
                  <ShopOutlined style={styles.sectionIcon} />
                  <span>Basic Information</span>
                </div>
                
                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={24} md={24} lg={24}>
                    <Form.Item label="Service Center Name" required style={{ marginBottom: 16 }}>
                      <Input
                        name="serviceCentreName"
                        value={values.serviceCentreName}
                        onChange={handleChange}
                        placeholder="Enter service center name"
                        style={styles.inputStyle}
                      //  prefix={<ShopOutlined style={{ color: "#bfbfbf" }} />}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={24} md={24} lg={24}>
                    {/*<FormItem label="Services Offered">*/}
                    {/*  <SelectSearch*/}
                    {/*      value={values.servicesOffered}*/}
                    {/*      items={servicesListOptions}*/}
                    {/*      setFieldValue={setFieldValue}*/}
                    {/*      fieldName="servicesOffered"*/}
                    {/*      placeholder="Services Offered"*/}
                    {/*      isMultiple*/}
                    {/*  />*/}
                    {/*</FormItem>*/}
                    <Form.Item label="Services Offered" style={{ marginBottom: 0 }}>
                      <SelectSearch
                          value={values.servicesOffered}
                          items={servicesListOptions}
                          setFieldValue={setFieldValue}
                          fieldName="servicesOffered"
                          placeholder="Services Offered"
                          isMultiple
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {/* Location Section */}
              <Card style={styles.sectionCard} size="small">
                <div style={styles.sectionTitle}>
                  <EnvironmentOutlined style={styles.sectionIcon} />
                  <span>Location Details</span>
                </div>
                
                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={24} md={24} lg={24}>
                    <Form.Item label="Full Address" required style={{ marginBottom: 16 }}>
                      <Input
                        name="formattedaddress"
                        value={values.formattedaddress}
                        onChange={handleChange}
                        placeholder="Enter complete address"
                        style={styles.inputStyle}
                       // prefix={<EnvironmentOutlined style={{ color: "#bfbfbf" }} />}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={24} md={24} lg={24}>
                    <Form.Item label="Address Name (Optional)" style={{ marginBottom: 16 }}>
                      <Input
                        name="addressName"
                        value={values.addressName}
                        onChange={handleChange}
                        placeholder="e.g., Main Branch, Downtown Location"
                        style={styles.inputStyle}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12} md={12} lg={12}>
                    <Form.Item label="Latitude" style={{ marginBottom: 16 }}>
                      <Input
                        name="latitude"
                        value={values.latitude}
                        onChange={handleChange}
                        placeholder="e.g., 40.7128"
                        style={styles.inputStyle}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={12} lg={12}>
                    <Form.Item label="Longitude" style={{ marginBottom: 16 }}>
                      <Input
                        name="longitude"
                        value={values.longitude}
                        onChange={handleChange}
                        placeholder="e.g., -74.0060"
                        style={styles.inputStyle}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={24} md={24} lg={24}>
                    <Form.Item label="Google Maps Link" style={{ marginBottom: 0 }}>
                      <Input
                        name="googleMapLink"
                        value={values.googleMapLink}
                        onChange={handleChange}
                        placeholder="https://maps.google.com/..."
                        style={styles.inputStyle}
                       // prefix={<LinkOutlined style={{ color: "#bfbfbf" }} />}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {/* Contact Section */}
              <Card style={styles.sectionCard} size="small">
                <div style={styles.sectionTitle}>
                  <PhoneOutlined style={styles.sectionIcon} />
                  <span>Contact Information</span>
                </div>
                
                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12} md={12} lg={12}>
                    <Form.Item label="Contact Person" style={{ marginBottom: 0 }}>
                      <Input
                        name="contactName"
                        value={values.contactName}
                        onChange={handleChange}
                        placeholder="Full name"
                        style={styles.inputStyle}
                       // prefix={<UserOutlined style={{ color: "#bfbfbf" }} />}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={12} lg={12}>
                    <Form.Item label="Phone Number" style={{ marginBottom: 0 }}>
                      <Input
                        name="contactNumber"
                        value={values.contactNumber}
                        onChange={handleChange}
                        placeholder="+92 3XX XXXXXXX"
                        style={styles.inputStyle}
                       /// prefix={<PhoneOutlined style={{ color: "#bfbfbf" }} />}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {/* Photo Upload Section */}
              <Card style={styles.sectionCard} size="small">
                <div style={styles.sectionTitle}>
                  <PictureOutlined style={styles.sectionIcon} />
                  <span>Service Center Photo</span>
                </div>
                
                <Upload.Dragger
                  name="photo"
                  multiple={false}
                  accept=".jpeg,.jpg,.png"
                  showUploadList={true}
                  style={styles.uploadDragger}
                  beforeUpload={async (file) => {
                    const unit8Arr = await fileToByteArray(file);
                    setFieldValue("photo", unit8Arr);
                    setFieldValue("photo_file_raw", file);
                  
                  }}
                >
                  <p className="ant-upload-drag-icon">
                    {uploadIcon}
                  </p>
                  <p className="ant-upload-text" style={{ margin: "8px 0" }}>
                    <strong>Click to upload</strong> or drag and drop
                  </p>
                  <p className="ant-upload-hint" style={{ color: "#8c8c8c", fontSize: "12px" }}>
                    PNG, JPG or JPEG (max. 5MB)
                  </p>
                </Upload.Dragger>
              </Card>

              {/* Availability Section */}
              <Card style={styles.sectionCard} size="small">
                <div style={styles.sectionTitle}>
                  <ClockCircleOutlined style={styles.sectionIcon} />
                  <span>Operating Hours</span>
                </div>

                {/* Header */}
                <div style={styles.availabilityHeader}>
                  <Text strong style={{ fontSize: "13px", color: "#595959" }}>Day</Text>
                  <Text strong style={{ fontSize: "13px", color: "#595959" }}>Opens At</Text>
                  <Text strong style={{ fontSize: "13px", color: "#595959" }}>Closes At</Text>
                </div>

                {/* Days */}
                {daysOfWeek.map(({ key, label }) => {
                  const dayData = values.availability[key];
                  
                  
                  return (
                    <div 
                      key={key} 
                      style={{
                        ...styles.availabilityRow,
                      }}
                    >
                      <div style={styles.dayLabel}>
                        <span>{label}</span>
                      </div>
                      
                      <Select
                        value={dayData?.open}
                        onChange={(value: string) =>
                          setFieldValue(`availability.${key}.open`, value)
                        }
                        showSearch
                        optionFilterProp="children"
                        style={styles.timeSelect}
                        size="middle"
                      >
                        {timeOptions.map((timeOption) => (
                          <Option key={timeOption} value={timeOption}>
                            {timeOption}
                          </Option>
                        ))}
                      </Select>

                      <Select
                        value={dayData?.closed}
                        onChange={(value: string) =>
                          setFieldValue(`availability.${key}.closed`, value)
                        }
                       
                        showSearch
                        optionFilterProp="children"
                        style={styles.timeSelect}
                        size="middle"
                      >
                        {timeOptions.map((timeOption) => (
                          <Option key={timeOption} value={timeOption}>
                            {timeOption}
                          </Option>
                        ))}
                      </Select>

                    </div>
                  );
                })}
              </Card>
            </div>

            {/* Action Buttons */}
            <div style={styles.actionContainer}>
              <Button 
                style={styles.cancelBtn} 
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                style={styles.submitBtn}
              >
                Add 
              </Button>
            </div>
          </Form>
        </div>
      )}
    </Formik>
  );
}